import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Sparkles,
  Loader2,
  ChefHat,
  Clock,
  Users,
  Save,
  Lock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MembershipDialog } from '../components/MembershipDialog';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Rotating friendly loader messages while AI crafts the recipe
const LOADER_MESSAGES_SK = [
  { icon: '🧺', text: 'Vyberám najlepšie ingrediencie…' },
  { icon: '📖', text: 'Listujem v Luciiných knihách inšpirácie…' },
  { icon: '🔍', text: 'Hľadám perfektnú kombináciu chutí…' },
  { icon: '🥄', text: 'Miešam, skúšam, dolaďujem…' },
  { icon: '🧂', text: 'Dochucujem so štipkou Magickej päťky…' },
  { icon: '👩‍🍳', text: 'Ešte pár krokov a recept je hotový!' },
];

const LOADER_MESSAGES_CZ = [
  { icon: '🧺', text: 'Vybírám nejlepší ingredience…' },
  { icon: '📖', text: 'Listuji v Luciiných knihách inspirace…' },
  { icon: '🔍', text: 'Hledám perfektní kombinaci chutí…' },
  { icon: '🥄', text: 'Míchám, ochutnávám, dolaďuji…' },
  { icon: '🧂', text: 'Dochucuji se špetkou Magické pětky…' },
  { icon: '👩‍🍳', text: 'Ještě pár kroků a recept je hotový!' },
];

export default function AIGeneratorPage() {
  const { t, language, user, loading: authStillLoading } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedRecipe, setGeneratedRecipe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);

  const loaderMessages = language === 'sk' ? LOADER_MESSAGES_SK : LOADER_MESSAGES_CZ;

  // Cycle loader messages every 2 seconds while loading
  useEffect(() => {
    if (!loading) {
      setMessageIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setMessageIndex((i) => (i + 1) % loaderMessages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [loading, loaderMessages.length]);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    setGeneratedRecipe(null);

    try {
      const response = await fetch(`${API_URL}/api/ai/generate-recipe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          prompt: prompt,
          language: language
        })
      });

      const data = await response.json();
      
      if (data.recipe) {
        setGeneratedRecipe(data.recipe);
        toast.success(language === 'sk' ? 'Recept vygenerovaný!' : 'Recept vygenerován!');
      } else {
        toast.error(language === 'sk' ? 'Nepodarilo sa vygenerovať recept' : 'Nepodařilo se vygenerovat recept');
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!generatedRecipe || !user) {
      toast.error(language === 'sk' ? 'Pre uloženie sa prihláste' : 'Pro uložení se přihlaste');
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/recipes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(generatedRecipe)
      });

      if (response.ok) {
        const savedRecipe = await response.json();
        toast.success(language === 'sk' ? 'Recept uložený!' : 'Recept uložen!');
        navigate(`/recipes/${savedRecipe.recipe_id}`);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  const suggestions = language === 'sk' ? [
    'Chcem niečo rýchle z kuracieho mäsa',
    'Zdravá večera bez mäsa',
    'Mám doma tekvicu a syr',
    'Tradičný slovenský obed',
    'Jednoduché raňajky do 10 minút'
  ] : [
    'Chci něco rychlého z kuřecího masa',
    'Zdravá večeře bez masa',
    'Mám doma dýni a sýr',
    'Tradiční český oběd',
    'Jednoduché snídaně do 10 minut'
  ];

  // Auth gate — AI generator is for members only
  if (authStillLoading) {
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) {
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="ai-generator-locked">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-text-heading mb-3">
            {language === 'sk' ? 'Vymyslieť recept' : 'Vymyslet recept'}
          </h1>
          <p className="text-muted-foreground mb-6 leading-relaxed">
            {language === 'sk'
              ? 'AI generátor receptov je súčasťou členstva v klube. Prihláste sa, alebo si zakúpte členstvo a vymýšľajte recepty z toho, čo máte doma.'
              : 'AI generátor receptů je součástí členství v klubu. Přihlaste se, nebo si zakupte členství a vymýšlejte recepty z toho, co máte doma.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/login')}
              className="bg-primary hover:bg-primary-hover text-white rounded-full px-6"
              data-testid="ai-generator-login-btn"
            >
              {language === 'sk' ? 'Prihlásiť sa' : 'Přihlásit se'}
            </Button>
            <MembershipDialog
              trigger={
                <Button
                  variant="outline"
                  className="rounded-full border-primary text-primary hover:bg-primary/5 px-6 w-full"
                  data-testid="ai-generator-buy-btn"
                >
                  {language === 'sk' ? 'Kúpiť členstvo' : 'Koupit členství'}
                </Button>
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-subtle py-8 md:py-16" data-testid="ai-generator-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-text-heading mb-4">
            {t('aiTitle')}
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            {t('aiDescription')}
          </p>
        </div>

        {/* Input area */}
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-soft border border-border/40 mb-8">
          <Textarea
            placeholder={t('aiPlaceholder')}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="min-h-[120px] text-lg border-0 focus-visible:ring-0 resize-none"
            data-testid="ai-prompt-input"
          />
          
          {/* Suggestions */}
          <div className="flex flex-wrap gap-2 mt-4 mb-6">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => setPrompt(suggestion)}
                className="px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>

          <Button
            onClick={handleGenerate}
            disabled={!prompt.trim() || loading}
            className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-lg btn-lift"
            data-testid="generate-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('generating')}
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                {t('generate')}
              </>
            )}
          </Button>
        </div>

        {/* AI Thinking modal — overlays the page so users can't miss it.
            Uses the same friendly rotating messages as before. */}
        <Dialog open={loading} onOpenChange={() => {}}>
          <DialogContent
            className="sm:max-w-md rounded-3xl p-8 md:p-10 [&>button]:hidden"
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            data-testid="ai-loading-modal"
          >
            <div className="flex flex-col items-center text-center">
              {/* Animated chef + sparkles */}
              <div className="relative w-20 h-20 mb-5">
                <div className="absolute inset-0 rounded-full bg-primary/10 animate-ping" />
                <div className="absolute inset-0 rounded-full bg-primary/20 animate-pulse" />
                <div className="relative w-full h-full rounded-full bg-gradient-to-br from-primary/15 to-secondary/15 flex items-center justify-center">
                  <ChefHat className="w-10 h-10 text-primary animate-bounce" style={{ animationDuration: '1.5s' }} />
                </div>
                <Sparkles
                  className="absolute -top-1 -right-1 w-6 h-6 text-accent-yellow animate-spin"
                  style={{ animationDuration: '3s' }}
                />
              </div>

              {/* Rotating friendly message */}
              <div className="min-h-[72px] flex flex-col items-center justify-center">
                <span className="text-4xl mb-2 transition-all duration-300" key={`icon-${messageIndex}`}>
                  {loaderMessages[messageIndex].icon}
                </span>
                <p
                  className="font-serif text-lg md:text-xl text-text-heading transition-opacity duration-500"
                  key={`text-${messageIndex}`}
                  data-testid="ai-loading-message"
                >
                  {loaderMessages[messageIndex].text}
                </p>
              </div>

              <p className="text-sm text-muted-foreground mt-4 max-w-sm">
                {language === 'sk'
                  ? 'Recept bude hotový za pár sekúnd. Držím palce, aby ste si ho naplno užili!'
                  : 'Recept bude hotový za pár sekund. Držím palce, ať si ho naplno užijete!'}
              </p>

              {/* Progress dots */}
              <div className="flex gap-1.5 mt-6">
                {loaderMessages.map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all duration-500 ${
                      i === messageIndex ? 'bg-primary w-6' : 'bg-primary/20'
                    }`}
                  />
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Generated Recipe */}
        {generatedRecipe && (
          <div className="bg-white rounded-3xl overflow-hidden shadow-hover border border-border/40 animate-slide-up" data-testid="generated-recipe">
            {/* Recipe header */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white shadow-soft flex items-center justify-center flex-shrink-0">
                  <ChefHat className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <span className="font-accent text-lg text-primary">Lucia odporúča</span>
                  <h2 className="font-serif text-2xl md:text-3xl font-semibold text-text-heading">
                    {generatedRecipe.name}
                  </h2>
                </div>
              </div>

              {/* Meta */}
              <div className="flex flex-wrap gap-4 mt-6">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4 text-primary" />
                  <span>{(generatedRecipe.prep_time || 0) + (generatedRecipe.cook_time || 0)} {t('minutes')}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-4 h-4 text-secondary" />
                  <span>{generatedRecipe.servings} {t('portions')}</span>
                </div>
              </div>
            </div>

            {/* Recipe content */}
            <div className="p-6 md:p-8 space-y-8">
              {/* Description */}
              {generatedRecipe.description && (
                <p className="text-muted-foreground leading-relaxed">
                  {generatedRecipe.description}
                </p>
              )}

              {/* Ingredients */}
              <div>
                <h3 className="font-serif text-xl font-semibold text-text-heading mb-4">
                  {t('ingredients')}
                </h3>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {generatedRecipe.ingredients?.map((ing, index) => (
                    <li key={index} className="flex items-center gap-2 text-muted-foreground">
                      <span className="w-2 h-2 rounded-full bg-primary" />
                      <span>
                        <span className="font-medium text-foreground">{ing.amount} {ing.unit}</span>{' '}
                        {ing.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Instructions */}
              <div>
                <h3 className="font-serif text-xl font-semibold text-text-heading mb-4">
                  {t('instructions')}
                </h3>
                <ol className="space-y-4">
                  {generatedRecipe.instructions?.map((step, index) => (
                    <li key={index} className="flex gap-4">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                        {index + 1}
                      </span>
                      <p className="text-muted-foreground leading-relaxed pt-0.5">
                        {step}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Tips */}
              {generatedRecipe.tips && (
                <div className="p-4 bg-accent-cream rounded-xl border border-accent-yellow/30">
                  <p className="font-accent text-lg text-primary mb-1">{t('tipFromLucia')}</p>
                  <p className="text-muted-foreground">{generatedRecipe.tips}</p>
                </div>
              )}

              {/* Save button */}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-secondary hover:bg-secondary-hover text-white rounded-full py-4"
                data-testid="save-recipe-button"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Save className="w-5 h-5 mr-2" />
                )}
                {t('saveRecipe')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
